"use client";
import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

// Small client-side helpers for the (otherwise server-rendered) Managed page:
// a "show more" toggle for long bullet lists and a single-open FAQ accordion.

export const Disclosure = ({
  moreLabel,
  lessLabel,
  children,
}: {
  moreLabel: string;
  lessLabel: string;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      {open && <div className="mt-2">{children}</div>}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-300 hover:text-red-200 transition-colors"
      >
        {open ? lessLabel : moreLabel}
        <svg
          viewBox="0 0 16 16"
          className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="m4 6 4 4 4-4" />
        </svg>
      </button>
    </div>
  );
};

export const FaqAccordion = ({ items }: { items: { q: string; a: string }[] }) => {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <dl className="divide-y divide-neutral-800 border-y border-neutral-800">
      {items.map((f, idx) => {
        const isOpen = open === idx;
        return (
          <div key={f.q}>
            <dt>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : idx)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-4 py-5 text-left"
              >
                <span className="text-base font-semibold text-white">{f.q}</span>
                <span
                  className={cn(
                    "inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border transition-colors",
                    isOpen
                      ? "border-red-500/50 bg-red-500/10 text-red-300"
                      : "border-neutral-700 text-neutral-400"
                  )}
                >
                  <svg
                    viewBox="0 0 16 16"
                    className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-45")}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    aria-hidden
                  >
                    <path d="M8 3v10M3 8h10" />
                  </svg>
                </span>
              </button>
            </dt>
            {isOpen && (
              <dd className="pb-5 pr-12 text-sm md:text-[15px] text-neutral-300 leading-relaxed">
                {f.a}
              </dd>
            )}
          </div>
        );
      })}
    </dl>
  );
};
